import {
  Button, Grid,
} from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { useEffect, useMemo, useState } from 'react';
import BlockData from '../../../models/BlockData';
import CarouselItem from './partials/CarouselItem';
import BlockCarouselItemData from '../../Blocks/BlockCarousel/BlockCarouselItemData';
import config from '../../../config';
import ModalComponent from '../../ModalComponent';
import { BlockHeight } from '../../../models/BlockSettings';

interface CarouselModalProps {
  carousel: BlockCarouselItemData[];
  slideId: string;
  auto: boolean;
  autoInterval: number;
  fixedAspectRatio: boolean;
  open: boolean;
  handleClose: () => void;
  handleSave: (newItems: BlockCarouselItemData[], auto: boolean, autoInterval: number, mode: 'slide' | 'fade') => void;
  blockData: BlockData;
  mode: 'slide' | 'fade';
  blockHeight: BlockHeight;
}

function CarouselModal({
  carousel,
  slideId,
  auto,
  open,
  blockData,
  fixedAspectRatio,
  handleClose,
  handleSave,
  autoInterval: autoIntervalDefault,
  mode,
  blockHeight,
}: CarouselModalProps) {
  const [managedItems, setManagedItems] = useState<BlockCarouselItemData[]>(carousel);
  const [managedAuto, setManagedAuto] = useState<boolean>(auto);
  const [autoInterval, setAutoInterval] = useState<number>(autoIntervalDefault);
  const [managedMode, setManagedMode] = useState<'slide' | 'fade'>(mode || 'slide');

  const [imagesToRemoveText, setImagesToRemoveText] = useState<Array<{ index: number }>>([]);

  const handleAdd = () => {
    setManagedItems([...managedItems, BlockCarouselItemData.getDefault(config.defaultImageUrl)]);
  };

  const handleSaveLocal = (newItems: BlockCarouselItemData[], auto: boolean, autoInterval: number, mode: 'slide' | 'fade') => {
    imagesToRemoveText.forEach((imageToRemove) => {
      newItems[imageToRemove.index].text = undefined;
      setManagedItems([...newItems]);
    });

    handleSave(newItems, auto, autoInterval, mode);
  };

  useEffect(() => {
    setAutoInterval(blockData.data.autoInterval ?? autoIntervalDefault);
    setManagedMode(blockData.data.mode ?? 'slide');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockData]);

  useEffect(() => {
    // clear imagesToRemoveText when modal is closed
    setImagesToRemoveText([]);
  }, [open]);

  const items = useMemo(() => {
    if (managedItems.length > 0) {
      return managedItems.map((carouselItem, index) => (
        <div className="card flex-row" key={carouselItem.id}>
          <div className="card-header card-header-settingSlider">
            <p className="settingSlider-title">
              {`Slide ${index + 1}`}
            </p>
          </div>
          <div className="card-body">
            <Grid item>
              <CarouselItem
                key={carouselItem.id}
                item={carouselItem}
                blockData={blockData}
                hasFixedAspectRatio={fixedAspectRatio}
                blockHeight={blockHeight}
                isTemplate={false}
                isEditing
                onRemoveBlock={() => { setManagedItems(managedItems.filter((item) => item !== carouselItem)); }}
                onChangeImage={(image) => {
                  managedItems[index].image = image;
                  setManagedItems([...managedItems]);
                }}
                onChangeLink={(link) => {
                  managedItems[index].link = link;
                  setManagedItems([...managedItems]);
                }}
                onChangeAlt={(alt) => {
                  managedItems[index].image.alt = alt;
                  setManagedItems([...managedItems]);
                }}
                onChangeText={(text) => {
                  managedItems[index].text = { value: text, settings: {} };
                  setManagedItems([...managedItems]);
                }}
                onRemoveText={() => {
                  managedItems[index].text = undefined;
                  setManagedItems([...managedItems]);
                  setImagesToRemoveText([...imagesToRemoveText, { index }]);
                }}
              />
            </Grid>
          </div>
        </div>
      ));
    }

    return [];
  }, [blockData, managedItems]);

  const [activeTab, setActiveTab] = useState<string>('settingSliderTab1');

  const stdCheckbox = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5em 2em',
    border: 'none',
  } as const;

  useEffect(() => {
    if (!open) {
      setManagedItems(carousel);
      setManagedAuto(auto);
    }
  }, [carousel, open, auto]);

  return (
    <ModalComponent
      size="xl"
      title="Slider settings"
      confirmButtonText="Save"
      onConfirm={() => handleSaveLocal(managedItems, managedAuto, autoInterval, managedMode)}
      show={open}
      closeModal={() => {
        setAutoInterval(autoIntervalDefault);
        setManagedMode(mode ?? 'slide');
        handleClose();
      }}
      centered
      animation={false}
      scrollable
      backdropClassName="z-index-1000"
    >
      <div className="modal-body p-3">
        <div className="text-sm mb-3">
          * Please check the actual slider behavior on the preview / published page
          <br />
          * If aspect ratios differ, parts of the image may be cropped or margins may appear
        </div>
        <nav className="nav nav-tabs mb-3">
          <a
            className={`nav-link ${activeTab === 'settingSliderTab1' ? 'active' : ''}`}
            href="#"
            onClick={() => setActiveTab('settingSliderTab1')}
          >
            Slider image settings
          </a>
          <a
            className={`nav-link ${activeTab === 'settingSliderTab2' ? 'active' : ''}`}
            href="#"
            onClick={() => setActiveTab('settingSliderTab2')}
          >
            Slider behavior settings
          </a>
        </nav>
        <div className="tab-content">
          <div
            className={`tab-pane ${activeTab === 'settingSliderTab1' ? 'active show' : ''}`}
          >
            <div className="d-flex d-flex flex-column h-100">
              <div className="d-flex flex-column">
                {items}

                <div className="card flex-row justify-content-center">
                  <Button color="primary" onClick={handleAdd}>
                    <AddCircleRoundedIcon />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`tab-pane ${activeTab === 'settingSliderTab2' ? 'active show' : ''}`}
          >
            <div className="row">
              <div className="col-12">
                <div className="card mb-0">
                  <div className="card-body py-2">
                    <div className="row">
                      <div className="col-12 mb-2">
                        <div className="text-bold mb-2">Autoplay</div>
                        <div style={stdCheckbox}>
                          <label>
                            <input
                              type="checkbox"
                              name="settingSliderAuto"
                              checked={managedAuto}
                              onChange={(event) => setManagedAuto(event.target.checked)}
                              className={`my-slide-toggle-${slideId}`}
                            />
                            Turn on autoplay
                          </label>
                        </div>
                      </div>
                      <hr />
                      <div className="col-12">
                        <div className="text-bold mb-2">Autoplay interval</div>
                        <input
                          type="number"
                          className="form-control mb-1 fa-align-left"
                          value={autoInterval}
                          onChange={(e) => {
                            setAutoInterval(parseInt(e.target.value, 10));
                          }}
                          style={{ width: '100px', textAlign: 'right' }}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                        <div className="text-sm">* Please enter seconds</div>
                      </div>
                      <hr />
                      <div className="col-12">
                        <div className="text-bold mb-2">Mode</div>
                        <div style={stdCheckbox}>
                          <label>
                            <input
                              type="radio"
                              name="settingSliderMode"
                              className={`my-slide-toggle-${slideId}`}
                              onChange={() => setManagedMode('slide')}
                              value="slide"
                              checked={managedMode === 'slide'}
                            />
                            Slide
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="settingSliderMode"
                              className={`my-slide-toggle-${slideId}`}
                              onChange={() => setManagedMode('fade')}
                              value="fade"
                              checked={managedMode === 'fade'}
                            />
                            Fade in/out
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
}

export default CarouselModal;
